/**
 * DynPointHandle — drags an XY point property of a dynamic block (AutoCAD's square grip).
 *
 * Property values live in the block definition's coordinate system, while the drag
 * happens in world space, so deltas are converted through the inverse of the block
 * reference transform.
 *
 * The value is written once, on release: each setValue() re-evaluates the block and
 * forces a full regen, which is far too costly to run on every pointer move. During
 * the drag the user sees a lightweight 2D overlay preview instead.
 */

import type { DRDrawingViewer } from "../../DRDrawingViewer";
import { trackShapeChange } from "@/analytics/drawing-analytics";
import type { CommandHistory } from "../../commands/CommandHistory";
import type { TransformContext, Point3D } from "../types";
import { HandleType, HandlePosition, HANDLE_SIZES } from "../types";
import { AbstractHandle } from "./AbstractHandle";
import type { DrawingModule } from "../../types/DrawingWeb";
import {
  setDynBlockProperty,
  listDynBlockProperties,
} from "../../blocks/dynamic";
import {
  SetDynBlockPropertyCommand,
  type DynBlockPropertyChange,
} from "../../commands/SetDynBlockPropertyCommand";
import { findEntityByHandle, openAs } from "../../core/oda";
import { deleteObj } from "../../core/delete";
import { OpenMode } from "../../core/constants";
import { selectionService } from "../../selection";
import { Logger } from "../../../logger";

const DYN_POINT_DRAW_OPTIONS = {
  fillColor: [70, 130, 220] as [number, number, number],
  strokeColor: [255, 255, 255] as [number, number, number],
  // Square: AutoCAD's point parameter grip.
  shape: "square" as const,
  radius: HANDLE_SIZES.vertex * HANDLE_SIZES.handleScale,
};

/** Below this world distance the drag is treated as a click and nothing is written. */
const MOVE_EPSILON = 0.001;

/** Half-size (screen px) of the cross drawn at the dragged position. */
const PREVIEW_CROSS_PX = 8;

export class DynPointHandle extends AbstractHandle {
  readonly type = HandleType.DynPoint;
  readonly position = HandlePosition.DynProperty;

  /** Positions benefit from object snapping, like vertex handles. */
  snapOptions = { acceptOrtho: true, acceptPolar: true, acceptObject: true };

  private startWorld: Point3D | null = null;
  private previewIds: string[] = [];

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    private readonly entityHandle: string,
    /** Property holding the X coordinate, e.g. "Position1 X". */
    private readonly propertyNameX: string,
    /** Property holding the Y coordinate, e.g. "Position1 Y". */
    private readonly propertyNameY: string,
    worldPosition: Point3D,
  ) {
    super(viewer, history);
    this.drawOptions = { ...DYN_POINT_DRAW_OPTIONS };
    this.worldPosition = { ...worldPosition };
  }

  start(context: TransformContext): void {
    if (this.isTransforming) return;
    this.isTransforming = true;
    this.startWorld = { ...context.worldPoint };
  }

  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;
    this.drawPreview(context.worldPoint);
  }

  end(context: TransformContext): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;
    this.clearPreview();

    const start = this.startWorld;
    this.startWorld = null;
    if (!start) return;

    const worldDelta = {
      x: context.worldPoint.x - start.x,
      y: context.worldPoint.y - start.y,
    };
    if (
      Math.abs(worldDelta.x) < MOVE_EPSILON &&
      Math.abs(worldDelta.y) < MOVE_EPSILON
    ) {
      return;
    }

    const entityId = findEntityByHandle(
      this.module,
      this.viewer.database,
      this.entityHandle,
    );
    if (!entityId || entityId.isNull()) return;

    try {
      const blockDelta = this.worldDeltaToBlockDelta(
        entityId,
        start,
        context.worldPoint,
      );
      if (!blockDelta) return;

      const changes: DynBlockPropertyChange[] = [];
      const currentX = this.readNumericProperty(entityId, this.propertyNameX);
      const currentY = this.readNumericProperty(entityId, this.propertyNameY);
      if (currentX === null || currentY === null) return;

      const changeX = setDynBlockProperty(
        this.module,
        entityId,
        this.propertyNameX,
        String(currentX + blockDelta.x),
      );
      if (changeX) changes.push(changeX);

      const changeY = setDynBlockProperty(
        this.module,
        entityId,
        this.propertyNameY,
        String(currentY + blockDelta.y),
      );
      if (changeY) changes.push(changeY);

      if (!changes.length) return;

      Logger.info(
        `DynPointHandle : ${this.propertyNameX}/${this.propertyNameY} moved`,
      );
      trackShapeChange("dyn_block_param_changed", {
        property: this.propertyNameX,
      });
      // Both coordinates undo together — a drag is one user action.
      this.history.execute(
        new SetDynBlockPropertyCommand(this.viewer, {
          blockHandle: this.entityHandle,
          changes,
        }),
      );

      this.viewer.forceRegen();
      selectionService.updateGrid();
    } finally {
      deleteObj(entityId);
    }
  }

  cancel(): void {
    if (!this.isTransforming) return;
    this.isTransforming = false;
    this.startWorld = null;
    this.clearPreview();
  }

  dispose(): void {
    super.dispose();
    this.clearPreview();
  }

  /**
   * Convert a world-space displacement into the block definition's coordinate system.
   * Both endpoints go through the inverse transform and are subtracted, so the
   * translation component cancels out and only rotation/scale remain.
   */
  private worldDeltaToBlockDelta(
    entityId: DrawingModule.OdDbObjectId,
    startWorld: Point3D,
    endWorld: Point3D,
  ): { x: number; y: number } | null {
    const blockRef = openAs(
      entityId,
      this.module.OdDbBlockReference,
      OpenMode.kForRead,
    );
    if (!blockRef) return null;

    const transform = blockRef.blockTransform();
    const inverse = transform.invert();

    const start = new this.module.OdGePoint3d(startWorld.x, startWorld.y, 0);
    const end = new this.module.OdGePoint3d(endWorld.x, endWorld.y, 0);
    start.transformBy(inverse);
    end.transformBy(inverse);

    const delta = { x: end.x - start.x, y: end.y - start.y };

    deleteObj(start);
    deleteObj(end);
    deleteObj(inverse);
    deleteObj(transform);
    deleteObj(blockRef);
    return delta;
  }

  /** Current numeric value of a dynamic property, or null when unreadable. */
  private readNumericProperty(
    entityId: DrawingModule.OdDbObjectId,
    propertyName: string,
  ): number | null {
    const prop = listDynBlockProperties(this.module, entityId).find(
      (candidate) => candidate.name === propertyName,
    );
    if (!prop) return null;
    const value = Number(prop.value);
    return Number.isFinite(value) ? value : null;
  }

  /** Draw a cross at the dragged position plus a rubber band from the origin. */
  private drawPreview(worldPoint: Point3D): void {
    this.clearPreview();
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const half = Math.abs(this.viewer.screenDistanceToWorld(PREVIEW_CROSS_PX));
    // Overlay styles take CSS colors, unlike handle draw options (RGB tuples).
    const style = { strokeColor: "rgb(70, 130, 220)" };

    this.previewIds.push(
      overlay.createPolyline(
        [
          { x: this.worldPosition.x, y: this.worldPosition.y },
          { x: worldPoint.x, y: worldPoint.y },
        ],
        { ...style, lineDash: [4, 4] },
      ),
      overlay.createPolyline(
        [
          { x: worldPoint.x - half, y: worldPoint.y },
          { x: worldPoint.x + half, y: worldPoint.y },
        ],
        style,
      ),
      overlay.createPolyline(
        [
          { x: worldPoint.x, y: worldPoint.y - half },
          { x: worldPoint.x, y: worldPoint.y + half },
        ],
        style,
      ),
    );
  }

  private clearPreview(): void {
    const overlay = this.viewer.overlayManager;
    if (overlay) this.previewIds.forEach((id) => overlay.remove(id));
    this.previewIds = [];
  }
}
